import { useEffect, useState } from 'react';
import { formatDate } from '../../../lib/format';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type {
  CertificateExpiryState, CertificateRow, ContractorRow, CursorPage, DesignationRow,
  EmployeeDetail as Employee, EmploymentRecordRow, ExaminationRow, FitnessDecision as Decision, JobRole, Plant,
} from '@dru/types';
import {
  BLOOD_GROUPS, EMPLOYMENT_CHANGE_LABEL, EMPLOYMENT_STATUS_LABEL, EMPLOYMENT_TYPE_LABEL, EXAM_TYPE_LABEL,
  FITNESS_LABEL, WORK_SHIFT_LABEL,
} from '@dru/types';
import {
  AddressBlock, addressLines, Alert, Button, Card, CardBody, CardHeader, DataTable, Detail,
  DetailHeader, Dialog, EntityCombobox, ErrorState, Facts, IconAction, InfoTip, RowActions, SearchSelect, SelectField, StatusBadge, Tabs, TextField,
  MoreActions, TextSkeleton, cn, fitnessTone, type Tone,
} from '@dru/ui';
import { ApiError } from '@dru/api-client';
import { EmployeeLabReports } from '../components/CompanyProfileReports';
import {
  ArrowLeftRight, CalendarPlus, CircleAlert, CircleCheck, CircleDashed, Clock, Download, Eye, Hourglass, LogOut,
  PauseCircle, Pencil, QrCode,
} from 'lucide-react';
import { AvatarButton, EmployeeAvatar, PhotoDialog, useEmployeePhoto } from '../components/EmployeePhoto';
import { api } from '../../../lib/api';
import { pageAllPlants } from '../../../lib/page';
import { fetchAll } from '../../../lib/fetchAll';
import { useAuth, errorMessage } from '../../../lib/auth';
import { useToast } from '@dru/ui';
import { DocumentsTab } from '../components/DocumentsTab';
import { TreatmentsTab } from '../components/TreatmentRecord';
import { ArrangeDialog, localDay } from '../components/ArrangeDialog';
import { CandidateDecisionDialog } from '../components/CandidateDecisionDialog';
import { useQuery } from '../../../lib/useQuery';
import { useBreadcrumbTitle } from '../../../layouts/BreadcrumbContext';
import { caseStatusLabel, caseStatusTone } from './Examinations';

/**
 * One worker, everything HR is entitled to see about them.
 *
 * The page opens on the question HR arrives with — is this person covered,
 * and is anything open — then the record. Actions follow the same split: the
 * header carries what is done to the worker's medical cover (schedule a
 * medical) and the everyday correction; the
 * employment events (status, transfer, exit) sit on the Employment tab beside
 * the history they write to.
 *
 * Tabs are shown only to a role that may open them — a plant login without
 * the documents permission is not offered a tab that answers 403.
 *
 * Nothing clinical appears anywhere on this page. The certificate shows a
 * number, a decision and a validity window — what the employer must act on —
 * and never a finding, a test value or a physician's reasoning. That is
 * enforced by the server's projection, not by this file. The health check
 * report tab is governed by the company's report policy, which the server
 * applies.
 */

// No "Occupational" tab: work area, shift, exposure and contract details are
// no longer part of the employee contract (docs/38 D7). A record that has
// them lists them under "Other recorded details" on the overview, and an old
// `?tab=occupational` link falls back to the overview.
const TAB_KEYS = ['overview', 'employment', 'cases', 'certificates', 'health', 'treatments', 'documents'] as const;
type Tab = (typeof TAB_KEYS)[number];

const statusTone = (s: string): Tone =>
  s === 'ACTIVE' ? 'covered'
  : s === 'CANDIDATE' ? 'info'
  : s === 'ON_LEAVE' ? 'pending'
  : s === 'SUSPENDED' ? 'warning'
  : 'neutral';

export const CERT_STATE: Record<CertificateExpiryState, { label: string; tone: Tone }> = {
  CURRENT: { label: 'Valid', tone: 'covered' },
  EXPIRING: { label: 'Expiring soon', tone: 'dueSoon' },
  EXPIRED: { label: 'Expired', tone: 'overdue' },
  REVOKED: { label: 'Revoked', tone: 'danger' },
  SUPERSEDED: { label: 'Replaced', tone: 'neutral' },
};

const LANGUAGE: Record<string, string> = { en: 'English', hi: 'Hindi', gu: 'Gujarati' };
const humanize = (v?: string | null) => (v ? v.charAt(0) + v.slice(1).toLowerCase().replace(/_/g, ' ') : '—');
const OPEN_CASE = (s: string) => !['CERTIFIED', 'CANCELLED', 'EXPIRED', 'NO_SHOW_CLOSED'].includes(s);

const day = (d?: string | null) => (d ? formatDate(d) : '—');

export function EmployeeDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { permissions } = useAuth();
  const can = (p: string) => permissions.has(p);
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();
  // The tab lives in the URL: a colleague sent the certificates tab opens
  // on it, and Back leaves a tab rather than the page.
  const asked = params.get('tab') as Tab | null;
  // Job roles are not assigned from here any more (docs/38): an old
  // "?assign=job-role" link simply opens the record.
  const [acting, setActing] = useState<null | 'transfer' | 'exit' | 'status' | 'edit' | 'arrange' | 'hire' | 'not-hired' | 'photo'>(null);
  const canRoles = can('jobrole.read');
  const jobRoles = useQuery<CursorPage<JobRole> | null>(
    () => (canRoles ? api.get<CursorPage<JobRole>>('/job-roles') : Promise.resolve(null)), [canRoles]);
  const jobRoleTitle = (rid?: string | null) => jobRoles.data?.items.find((r) => r.id === rid)?.title ?? null;

  const employee = useQuery<Employee>(() => api.get<Employee>(`/employees/${id}`), [id]);
  // Opened from the Employees table's row icons: ?action=edit opens the edit
  // dialog, ?action=exit the exit dialog — once the record is in, and only
  // for a login that may do it. The parameter is dropped so Back and reload
  // do not reopen it.
  const action = params.get('action');
  useEffect(() => {
    if (!action || !employee.data) return;
    if (action === 'edit' && can('employee.update')) setActing('edit');
    if (action === 'exit' && can('employee.deactivate')) setActing('exit');
    const next = new URLSearchParams(params); next.delete('action'); setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, employee.data]);
  useBreadcrumbTitle(employee.data?.fullName);
  // Fetched through a grant, never a URL; re-fetched when the photo changes.
  const photo = useEmployeePhoto(employee.data?.id, employee.data?.hasPhoto, employee.data?.photoUpdatedAt);

  // The short reference lists every tab needs to turn a foreign key into a
  // name. Fetched once here rather than per tab.
  const plants = useQuery<CursorPage<Plant>>(
    () => api.get<CursorPage<Plant>>('/plants?limit=100'), []);
  const designations = useQuery<DesignationRow[]>(
    () => fetchAll<DesignationRow>((cursor) => api.get<CursorPage<DesignationRow>>(
      `/designations?limit=200${cursor ? `&cursor=${cursor}` : ''}`,
    )), []);
  const contractors = useQuery<ContractorRow[]>(
    () => fetchAll<ContractorRow>((cursor) => api.get<CursorPage<ContractorRow>>(
      `/contractors?limit=200${cursor ? `&cursor=${cursor}` : ''}`,
    )), []);

  // Cover and open cases: read once, shown on the overview and in their tabs.
  const canCases = can('examination.read');
  const canCerts = can('certificate.read');
  const cases = useQuery<CursorPage<ExaminationRow> | null>(
    () => (canCases ? api.get<CursorPage<ExaminationRow>>(`/examinations?employeeId=${id}&limit=50`) : Promise.resolve(null)),
    [id, canCases],
  );
  const certs = useQuery<CursorPage<CertificateRow> | null>(
    () => (canCerts ? api.get<CursorPage<CertificateRow>>(`/certificates?employeeId=${id}&limit=50`) : Promise.resolve(null)),
    [id, canCerts],
  );

  const plantName = (pid?: string | null) =>
    plants.data?.items.find((p) => p.id === pid)?.name ?? '—';
  const desigName = (did?: string | null) =>
    designations.data?.find((d) => d.id === did)?.title ?? '—';
  const contractorName = (cid?: string | null) =>
    contractors.data?.find((c) => c.id === cid)?.name ?? '—';

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'employment', label: 'Employment' },
    { key: 'cases', label: 'Medical cases', visible: canCases, hint: cases.data?.items.filter((c) => OPEN_CASE(c.status)).length || undefined },
    { key: 'certificates', label: 'Certificates', visible: canCerts },
    { key: 'health', label: 'Health reports', visible: can('report.health-read') },
    { key: 'treatments', label: 'Treatments', visible: can('treatment.read') },
    { key: 'documents', label: 'Documents', visible: can('employeedoc.read') },
  ];
  const tab: Tab = asked && TAB_KEYS.includes(asked) && tabs.find((t) => t.key === asked)?.visible !== false ? asked : 'overview';
  const setTab = (k: string) => setParams((p) => {
    const n = new URLSearchParams(p); if (k === 'overview') n.delete('tab'); else n.set('tab', k); n.delete('assign'); return n;
  });

  if (employee.error) {
    return (
      <div className="space-y-4">
        <ErrorState title="This employee could not be opened" detail={employee.error} onRetry={employee.reload} />
      </div>
    );
  }

  const e = employee.data;
  const live = e && !['EXITED', 'NOT_HIRED'].includes(e.status);

  // The certificate that covers them today: the latest-running one not
  // revoked. A lapsed one still says when cover ended.
  const certList = certs.data?.items ?? [];
  const current = [...certList]
    // Nor one a later certificate replaced: it can run longer than the one
    // that stands (a 12-month certificate renewed on a 3-month protocol) and
    // would otherwise be shown as the worker's cover.
    .filter((c) => !['REVOKED', 'SUPERSEDED'].includes(c.status)
      && c.expiryState !== 'REVOKED' && c.expiryState !== 'SUPERSEDED')
    .sort((x, y) => y.validUntil.localeCompare(x.validUntil))[0];
  const openCase = (cases.data?.items ?? []).find((c) => OPEN_CASE(c.status));
  // The latest signed case, when it ended without a certificate: found unfit,
  // or temporarily unfit. With no certificate on file this box read "No
  // certificate yet", as though the worker had simply not been seen.
  const lastSigned = (cases.data?.items ?? []).find((c) => c.status === 'CERTIFIED' && c.outcome);
  const notCleared = !current && lastSigned && (lastSigned.outcome === 'UNFIT' || lastSigned.outcome === 'TEMPORARILY_UNFIT' || lastSigned.outcome === 'FURTHER_EVALUATION')
    ? lastSigned : null;

  return (
    <div className="space-y-5">
      <DetailHeader
        // The face beside the name. Pressing it changes the photo, for a
        // login that may edit the record; an exited worker's stays as it was.
        leading={e && (
          can('employee.update') && live
            ? <AvatarButton name={e.fullName} src={photo} onClick={() => setActing('photo')} label={e.hasPhoto ? 'Change photo' : 'Add photo'} />
            : <EmployeeAvatar name={e.fullName} src={photo} />
        )}
        eyebrow={e?.status === 'CANDIDATE' ? 'Pre-employment candidate' : undefined}
        title={e ? e.fullName : 'Loading…'}
        badges={e && (
          <StatusBadge tone={statusTone(e.status)}>
            {EMPLOYMENT_STATUS_LABEL[e.status as keyof typeof EMPLOYMENT_STATUS_LABEL] ?? humanize(e.status)}
          </StatusBadge>
        )}
        subtitle={e && (
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>Employee ID <span className="font-mono text-neutral-700">{e.employeeCode}</span></span>
            <span aria-hidden>·</span>
            <span>{plantName(e.plantId)}</span>
            <span aria-hidden>·</span>
            <span>{EMPLOYMENT_TYPE_LABEL[e.employmentType] ?? humanize(e.employmentType)}</span>
          </span>
        )}
        // The three things HR does from a record: book the medical, correct
        // the details, print the ID card. Candidate decisions sit beside them
        // only for a candidate; employment events live on the Employment tab.
        actions={e && live && (
          <>
            {can('examination.create') && (
              <Button icon={<CalendarPlus className="h-4 w-4" />} onClick={() => setActing('arrange')}>
                Schedule checkup
              </Button>
            )}
            {can('employee.update') && (
              <Button variant="secondary" icon={<Pencil className="h-4 w-4" />} onClick={() => setActing('edit')}>
                Edit employee
              </Button>
            )}
            {/* The ID slip: name, code, photo and the employee code as a QR —
                what the check-in desk verifies off the card. Same tab, not a
                new one: an impersonation session lives in this tab's memory
                only, and a new tab resumed as the administrator, who has no
                company, so the slip failed to load. It has its own "Back to
                record" link. */}
            <Button
              variant="secondary"
              icon={<QrCode className="h-4 w-4" />}
              onClick={() => navigate(`/hr/employees/${e.id}/print-qr`)}
            >
              Print QR card
            </Button>
            {e.status === 'CANDIDATE' && can('employee.update') && (
              <MoreActions label="Candidate">
                <Button variant="secondary" onClick={() => setActing('hire')}>Mark hired</Button>
                <Button variant="secondary" onClick={() => setActing('not-hired')}>Close candidate</Button>
              </MoreActions>
            )}
          </>
        )}
      />

      {/* An exited worker's record stays readable, and says so rather than
          leaving the reader to infer it from missing buttons. */}
      {e?.status === 'EXITED' && (
        <Alert tone="info" title={`Exited ${day(e.exitedOn)}`}>
          {e.exitReason ? `${e.exitReason}. ` : ''}
          Their record is retained: medical cases, certificates and consents from
          their employment are not deleted, and their app sign-in is closed.
        </Alert>
      )}
      {e?.status === 'CANDIDATE' && (
        <Alert tone="info" title="Pre-employment candidate">
          Only a pre-employment medical case can be arranged. This person is not counted in
          workforce compliance until they are marked hired.
        </Alert>
      )}
      {e?.status === 'NOT_HIRED' && (
        <Alert tone="info" title="Candidate process closed">
          Past medical cases and reports are retained. No new medical case can be arranged
          from this closed candidate record.
        </Alert>
      )}

      <Tabs tabs={tabs} active={tab} onChange={setTab} />

      {employee.loading || !e ? (
        <TextSkeleton />
      ) : (
        <>
          {tab === 'overview' && (
            <div className="space-y-4">
              {(canCerts || canCases) && e.status !== 'NOT_HIRED' && (
                <FitnessPanel
                  loading={(certs.loading || cases.loading) && !certs.data && !cases.data}
                  canCerts={canCerts}
                  canCases={canCases}
                  current={current}
                  notCleared={notCleared}
                  openCase={openCase}
                  canArrange={Boolean(live && can('examination.create'))}
                  onAllCertificates={() => setTab('certificates')}
                />
              )}

              {/* The employee contract (docs/38 D7): the same eleven facts the
                  import template and the add form carry, in the same order. */}
              <Card>
                {/* Name, ID, plant and employment type are in the header above. */}
                <CardHeader title="Personal details" />
                <CardBody className="pt-0">
                  <dl className="grid gap-x-8 gap-y-4 max-sm:grid-cols-2 max-sm:gap-x-4 [&>*]:min-w-0 [&>*]:break-words sm:grid-cols-2 lg:grid-cols-3">
                    <Detail label="Gender" value={humanize(e.sex)} />
                    <Detail label="Date of birth" value={day(e.dateOfBirth)} />
                    <Detail label="Mobile number" value={e.mobile ?? '—'} mono />
                    <Detail label="Email" value={e.email ?? '—'} />
                    <Detail label="Blood group" value={e.bloodGroup ?? '—'} />
                    <Detail
                      label="Joining date"
                      value={e.status === 'CANDIDATE' || e.status === 'NOT_HIRED' ? 'Not joined' : day(e.joinedOn)}
                    />
                  </dl>
                </CardBody>
              </Card>

              <OtherRecordedDetails
                employee={e}
                designation={e.designationId ? desigName(e.designationId) : null}
                jobRole={e.jobRoleId ? (jobRoleTitle(e.jobRoleId) ?? 'Assigned') : null}
                contractor={e.contractorId ? contractorName(e.contractorId) : null}
              />
            </div>
          )}

          {tab === 'employment' && (
            <EmploymentTab
              employee={e}
              plantName={plantName}
              actions={live && e.status !== 'CANDIDATE' && (
                <>
                  {can('employee.update') && (
                    <Button size="sm" variant="secondary" icon={<PauseCircle className="h-4 w-4" />} onClick={() => setActing('status')}>
                      Change status
                    </Button>
                  )}
                  {can('employee.transfer') && (
                    <Button size="sm" variant="secondary" icon={<ArrowLeftRight className="h-4 w-4" />} onClick={() => setActing('transfer')}>
                      Transfer
                    </Button>
                  )}
                  {can('employee.deactivate') && (
                    <Button size="sm" variant="secondary" className="text-danger-600" icon={<LogOut className="h-4 w-4" />} onClick={() => setActing('exit')}>
                      Record exit
                    </Button>
                  )}
                </>
              )}
            />
          )}

          {tab === 'cases' && <CasesTab list={cases} canArrange={Boolean(live && can('examination.create'))} onArrange={() => setActing('arrange')} />}
          {tab === 'certificates' && <CertificatesTab list={certs} />}
          {tab === 'health' && <EmployeeLabReports employeeId={id} />}
          {tab === 'treatments' && <TreatmentsTab employee={e} />}
          {tab === 'documents' && <DocumentsTab employeeId={id} />}
        </>
      )}

      {acting === 'transfer' && e && (
        <TransferDialog
          employee={e}
          plants={plants.data?.items ?? []}
          onClose={() => setActing(null)}
          onDone={() => { setActing(null); employee.reload(); toast('success', 'Transfer recorded.'); }}
        />
      )}
      {acting === 'exit' && e && (
        <ExitDialog
          employee={e}
          onClose={() => setActing(null)}
          onDone={() => { setActing(null); employee.reload(); toast('success', 'Exit recorded.'); }}
        />
      )}
      {acting === 'status' && e && (
        <StatusDialog
          employee={e}
          onClose={() => setActing(null)}
          onDone={() => { setActing(null); employee.reload(); toast('success', 'Status changed.'); }}
        />
      )}
      {(acting === 'hire' || acting === 'not-hired') && e && (
        <CandidateDecisionDialog
          employee={e}
          decision={acting === 'hire' ? 'HIRE' : 'NOT_HIRED'}
          onClose={() => setActing(null)}
          onDone={() => { setActing(null); employee.reload(); }}
        />
      )}
      {acting === 'arrange' && e && (
        <ArrangeDialog
          employee={e}
          onClose={() => setActing(null)}
          onDone={() => { setActing(null); employee.reload(); cases.reload(); toast('success', 'Medical case arranged.'); }}
        />
      )}
      {acting === 'edit' && e && (
        <EditDialog
          employee={e}
          designation={e.designationId ? desigName(e.designationId) : ''}
          onClose={() => setActing(null)}
          onDone={() => { setActing(null); employee.reload(); toast('success', 'Details saved.'); }}
        />
      )}
      {acting === 'photo' && e && (
        <PhotoDialog
          name={e.fullName}
          hasPhoto={e.hasPhoto}
          onClose={() => setActing(null)}
          onPick={async (shot) => {
            await api.put(`/employees/${e.id}/photo`, { mimeType: shot.mimeType, content: shot.content });
            setActing(null); employee.reload(); toast('success', 'Photo saved.');
          }}
          onRemove={async () => {
            await api.del(`/employees/${e.id}/photo`);
            setActing(null); employee.reload(); toast('success', 'Photo removed.');
          }}
        />
      )}
    </div>
  );
}

/**
 * Is this person fit to work, until when, and what happens next — answered
 * in one line before any detail.
 *
 * The headline is the cover state (the same words as the dashboard tiles);
 * under it the latest certificate, its validity, when the next checkup is
 * due, and any case already under way. Never a finding: the certificate's
 * decision and dates are what the employer must act on, and that is all the
 * server sends.
 */
function FitnessPanel({
  loading, canCerts, canCases, current, notCleared, openCase, canArrange, onAllCertificates,
}: {
  loading: boolean;
  canCerts: boolean;
  canCases: boolean;
  current?: CertificateRow;
  notCleared: ExaminationRow | null;
  openCase?: ExaminationRow;
  canArrange: boolean;
  onAllCertificates: () => void;
}) {
  const { permissions } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (loading) {
    return <Card><CardBody><TextSkeleton lines={3} /></CardBody></Card>;
  }

  const state = current?.expiryState;
  // One headline, by what HR has to do about it.
  const head: { label: string; tone: Tone; icon: typeof CircleCheck; line: string } =
    notCleared
      ? { label: 'Not cleared', tone: 'overdue', icon: CircleAlert, line: 'Examined, but no fitness certificate was issued for this outcome.' }
      : current && state === 'EXPIRED'
        ? { label: 'Not covered', tone: 'overdue', icon: CircleAlert, line: `Certificate expired on ${day(current.validUntil)}.` }
        : current && state === 'EXPIRING'
          ? { label: 'Due soon', tone: 'dueSoon', icon: Clock, line: `Covered until ${day(current.validUntil)}. Book the next checkup before then.` }
          : current
            ? { label: 'Covered', tone: 'covered', icon: CircleCheck, line: `${FITNESS_LABEL[current.decision as Decision] ?? 'Covered'} until ${day(current.validUntil)}.` }
            : openCase
              // Not covered yet, but the first checkup is already booked or
              // under way: the work is in hand, not missing.
              ? { label: 'Checkup in progress', tone: 'pending', icon: Hourglass, line: canCerts ? 'No fitness certificate yet — it is issued once the physician signs.' : '' }
              : !canCerts
                ? { label: 'No open case', tone: 'neutral', icon: CircleDashed, line: '' }
                : { label: 'Never checked', tone: 'overdue', icon: CircleDashed, line: 'No fitness certificate on record yet.' };

  // The next step, in words. A case already under way is the step.
  const needsCheckup = !current || state === 'EXPIRED' || state === 'EXPIRING' || Boolean(notCleared);
  const next = openCase
    ? 'Next step: follow the current case below.'
    : needsCheckup && canArrange ? 'Next step: schedule a checkup.' : null;

  const download = async (id: string) => {
    setDownloading(true); setError(null);
    try {
      const grant = await api.get<{ url: string }>(`/certificates/${id}/download`);
      window.open(grant.url, '_blank', 'noopener,noreferrer');
    } catch (err) { setError(errorMessage(err)); } finally { setDownloading(false); }
  };

  const Icon = head.icon;
  const toneText: Record<string, string> = {
    covered: 'text-covered-600', dueSoon: 'text-dueSoon-600', overdue: 'text-overdue-600',
    pending: 'text-pending-600', neutral: 'text-neutral-600',
  };

  return (
    <Card>
      <CardHeader
        title="Fitness status"
        hint="What the employer must act on: the fitness outcome and how long it holds. Findings stay with the physician."
      />
      <CardBody className="space-y-4 pt-0">
        <div className="flex items-start gap-3">
          <Icon aria-hidden className={cn('mt-0.5 h-7 w-7 shrink-0', toneText[head.tone] ?? 'text-neutral-600')} />
          <div className="min-w-0 flex-1">
            <p className={cn('text-h3', toneText[head.tone] ?? 'text-neutral-900')}>{head.label}</p>
            {head.line && <p className="text-body text-neutral-700">{head.line}</p>}
            {/* Words only: the Schedule checkup button is in the header. */}
            {next && <p className="mt-1 text-small font-medium text-neutral-700">{next}</p>}
          </div>
        </div>

        {error && <Alert tone="danger">{error}</Alert>}

        <Facts
          className="max-sm:grid-cols-2"
          columns={canCerts && canCases ? 4 : 3}
          facts={[
            canCerts && {
              label: 'Latest certificate',
              value: current
                ? (
                  <span className="inline-flex flex-wrap items-center gap-2">
                    <Link to={`/hr/certificates/${current.id}`} className="inline-flex min-h-6 items-center tap-area">
                      <StatusBadge tone={fitnessTone(current.decision)}>
                        {FITNESS_LABEL[current.decision as Decision] ?? humanize(current.decision)}
                      </StatusBadge>
                    </Link>
                    {permissions.has('certificate.download') && (
                      <IconAction
                        label={current.generationStatus !== 'ISSUED'
                          ? 'The certificate PDF is still being generated'
                          : `Download certificate ${current.certificateNumber} (PDF)`}
                        icon={Download}
                        loading={downloading}
                        disabled={current.generationStatus !== 'ISSUED'}
                        onClick={() => void download(current.id)}
                      />
                    )}
                  </span>
                )
                : notCleared
                  ? (
                    <Link to={`/hr/examinations/${notCleared.id}`} className="inline-flex min-h-6 items-center tap-area">
                      <StatusBadge tone={fitnessTone(notCleared.outcome!)}>{FITNESS_LABEL[notCleared.outcome as Decision]}</StatusBadge>
                    </Link>
                  )
                  : <span className="text-neutral-600">None yet</span>,
              hint: current
                ? (
                  <button type="button" onClick={onAllCertificates}
                          className="font-mono text-primary-700 hover:underline max-md:min-h-10">
                    {current.certificateNumber}
                  </button>
                )
                : undefined,
            },
            canCerts && {
              label: 'Valid until',
              value: current ? day(current.validUntil) : '—',
              hint: state ? <StatusBadge tone={CERT_STATE[state].tone}>{CERT_STATE[state].label}</StatusBadge> : undefined,
            },
            canCerts && {
              label: 'Next checkup due',
              // Renewal is due when the cover ends; with no cover, it is due now.
              value: current && state !== 'EXPIRED' && !notCleared
                ? day(current.validUntil)
                : openCase
                  ? <span className="text-pending-600">In progress</span>
                  : <span className="font-medium text-overdue-600">Now</span>,
            },
            canCases && {
              label: 'Current case',
              value: openCase
                ? (
                  <Link to={`/hr/examinations/${openCase.id}`} className="inline-flex min-h-6 items-center tap-area text-primary-700 hover:underline">
                    {EXAM_TYPE_LABEL[openCase.examinationType] ?? humanize(openCase.examinationType)}
                  </Link>
                )
                : <span className="text-neutral-600">None open</span>,
              hint: openCase
                ? <StatusBadge tone={caseStatusTone(openCase.status)}>{caseStatusLabel(openCase.status)}</StatusBadge>
                : undefined,
            },
          ]}
        />
      </CardBody>
    </Card>
  );
}

/**
 * Correcting the record.
 *
 * The employee contract HR can correct (docs/38 D7): name, gender, date of
 * birth, mobile, email, blood group and joining date — plus the
 * language their reminders go out in. A mistyped mobile is the one that hurt:
 * it is how the worker signs in to the app. Plant and status stay out: they
 * are events (transfer, exit) with their own history rows.
 *
 * Designation, work area, shift and exposure are no longer edited here, and
 * are deliberately left out of the PATCH rather than sent as null — a record
 * that has them keeps them. Nothing here demands a value a legacy record
 * never had: a blank stays blank.
 */
function EditDialog({ employee: e, designation, onClose, onDone }: {
  employee: Employee;
  /** The current designation's title ("—" when unknown). */
  designation: string;
  onClose: () => void; onDone: () => void;
}) {
  const designationNow = designation === '—' ? '' : designation;
  const joined = e.status !== 'CANDIDATE' && e.status !== 'NOT_HIRED';
  const [f, setF] = useState({
    fullName: e.fullName, dateOfBirth: e.dateOfBirth?.slice(0, 10) ?? '', sex: e.sex ?? '',
    mobile: e.mobile ?? '', email: e.email ?? '',
    preferredLocale: e.preferredLocale ?? 'en',
    bloodGroup: e.bloodGroup ?? '',
    joinedOn: e.joinedOn?.slice(0, 10) ?? '',
    // Employment details, filled in here since Add stopped asking (29 Oct 2026).
    designation: designationNow,
    natureOfWork: e.natureOfWork ?? '',
    employmentType: e.employmentType === 'CONTRACT' ? 'CONTRACT' : 'DIRECT',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const set = (k: keyof typeof f) => (v: string) => {
    setF((p) => ({ ...p, [k]: v }));
    setFieldErrors((p) => { const { [k]: _, ...rest } = p; return rest; });
  };
  const today = localDay();

  async function save() {
    const local: Record<string, string> = {};
    if (f.fullName.trim().length < 2) local.fullName = 'Full name is required';
    if (f.dateOfBirth && f.dateOfBirth > today) local.dateOfBirth = 'Date of birth cannot be in the future.';
    else if (f.dateOfBirth && Number(f.dateOfBirth.slice(0, 4)) < 1900) local.dateOfBirth = 'Enter a valid date of birth.';
    if (f.mobile && !/^[6-9]\d{9}$/.test(f.mobile)) local.mobile = 'Enter a valid 10-digit mobile number';
    if (f.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)) local.email = 'Enter a valid email address';
    if (f.joinedOn && f.dateOfBirth && f.joinedOn <= f.dateOfBirth) local.joinedOn = 'Joining date must be after the date of birth.';
    setFieldErrors(local);
    if (Object.keys(local).length) { setError('Please correct the highlighted fields.'); return; }
    setBusy(true); setError(null);
    try {
      const nullable = (v: string) => (v.trim() ? v.trim() : null);
      await api.patch(`/employees/${e.id}`, {
        fullName: f.fullName.trim(),
        dateOfBirth: nullable(f.dateOfBirth),
        sex: nullable(f.sex),
        // The API rejects an empty string here; a blank means "leave it".
        ...(f.mobile.trim() ? { mobile: f.mobile.trim() } : {}),
        ...(f.email.trim() ? { email: f.email.trim() } : {}),
        preferredLocale: f.preferredLocale,
        bloodGroup: nullable(f.bloodGroup),
        // Sent only when changed: a joining date is corrected, never cleared,
        // and a candidate's comes from the hire.
        ...(joined && f.joinedOn && f.joinedOn !== (e.joinedOn?.slice(0, 10) ?? '') ? { joinedOn: f.joinedOn } : {}),
        // Each sent only when changed, so a record keeps what it had.
        ...(f.designation.trim() !== designationNow ? { designation: f.designation.trim() } : {}),
        ...(f.natureOfWork.trim() !== (e.natureOfWork ?? '') ? { natureOfWork: nullable(f.natureOfWork) } : {}),
        ...(f.employmentType !== (e.employmentType === 'CONTRACT' ? 'CONTRACT' : 'DIRECT') ? { employmentType: f.employmentType } : {}),
        version: e.version,
      });
      onDone();
    } catch (err) {
      if (err instanceof ApiError) setFieldErrors(err.fieldErrors);
      setError(errorMessage(err));
    } finally { setBusy(false); }
  }

  return (
    <Dialog title={`Edit ${e.fullName}`} onClose={onClose} wide>
      <div className="mt-2 grid gap-3 sm:grid-cols-2">
        <TextField label="Full name" value={f.fullName} onChange={set('fullName')} required error={fieldErrors.fullName} />
        <SelectField label="Gender" value={f.sex} onChange={set('sex')} error={fieldErrors.sex}>
          <option value="">Not recorded</option>
          <option value="MALE">Male</option>
          <option value="FEMALE">Female</option>
          <option value="OTHER">Other</option>
        </SelectField>
        <TextField label="Date of birth" type="date" value={f.dateOfBirth} onChange={set('dateOfBirth')} error={fieldErrors.dateOfBirth} />
        <TextField label="Mobile number" value={f.mobile} mono inputMode="numeric" error={fieldErrors.mobile}
          onChange={(v) => set('mobile')(v.replace(/\D/g, '').slice(0, 10))}
          hint="Changing it changes who can sign in."
          info="How they sign in to the employee app." />
        <TextField label="Email" type="email" value={f.email} onChange={set('email')} error={fieldErrors.email} />
        <SearchSelect label="Blood group" value={f.bloodGroup} onChange={set('bloodGroup')} error={fieldErrors.bloodGroup}
          placeholder="Not recorded" searchPlaceholder="Search blood groups…"
          options={[{ value: '', label: 'Not recorded' }, ...BLOOD_GROUPS.map((g) => ({ value: g, label: g }))]} />
        {joined && (
          <TextField label="Joining date" type="date" value={f.joinedOn} onChange={set('joinedOn')} error={fieldErrors.joinedOn} />
        )}
        <TextField label="Designation" value={f.designation} onChange={(v) => set('designation')(v.slice(0, 120))}
          error={fieldErrors.designation} placeholder="For example, Machine operator" info="Printed on their fitness certificate." />
        <TextField label="Nature of work" value={f.natureOfWork} onChange={(v) => set('natureOfWork')(v.slice(0, 200))}
          error={fieldErrors.natureOfWork} placeholder="For example, Welding, work at height" />
        <SelectField label="Employment type" value={f.employmentType} onChange={set('employmentType')} error={fieldErrors.employmentType}>
          <option value="DIRECT">Direct</option>
          <option value="CONTRACT">Contractor</option>
        </SelectField>
        <SelectField label="Preferred language" value={f.preferredLocale} onChange={set('preferredLocale')}
          info="The language of their reminders and consent notice.">
          <option value="en">English</option>
          <option value="hi">Hindi</option>
          <option value="gu">Gujarati</option>
        </SelectField>
      </div>
      {error && <Alert className="mt-3">{error}</Alert>}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={save} loading={busy}>Save changes</Button>
      </div>
    </Dialog>
  );
}

/**
 * What a record carries beyond the employee contract — designation, job
 * role, home address, occupational profile, contract — from before docs/38
 * or from an older import file. No longer asked for, but never hidden from
 * the record that has it: listed read-only, folded, and only when present.
 */
function OtherRecordedDetails({ employee: e, designation, jobRole, contractor }: {
  employee: Employee; designation: string | null; jobRole: string | null; contractor: string | null;
}) {
  const facts: Array<[string, React.ReactNode]> = [];
  const add = (label: string, value: React.ReactNode | null | undefined) => {
    if (value) facts.push([label, value]);
  };
  add('Designation', designation);
  add('Nature of work', e.natureOfWork);
  add('Job role', jobRole);
  add('Work area', e.workArea);
  add('Shift', e.shift ? (WORK_SHIFT_LABEL[e.shift] ?? humanize(e.shift)) : null);
  add('Exposure', e.exposureType);
  add('Contractor', contractor);
  add("Contractor's employee ID", e.contractorEmployeeCode);
  add('Contract start', e.contractStartDate ? day(e.contractStartDate) : null);
  add('Contract end', e.contractEndDate
    ? (new Date(e.contractEndDate) < new Date()
      ? <StatusBadge tone="overdue">Ended {day(e.contractEndDate)}</StatusBadge>
      : day(e.contractEndDate))
    : null);
  if (e.preferredLocale && e.preferredLocale !== 'en') {
    add('Preferred language', LANGUAGE[e.preferredLocale] ?? e.preferredLocale);
  }
  const hasAddress = addressLines(e).length > 0;
  if (!facts.length && !hasAddress) return null;

  return (
    <Card>
      <details>
        <summary className="flex min-h-12 cursor-pointer items-center px-5 py-3 text-small font-semibold text-neutral-800">
          Other recorded details
        </summary>
        <CardBody className="space-y-4 pt-0">
          {facts.length > 0 && (
            <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
              {facts.map(([label, value]) => <Detail key={label} label={label} value={value} />)}
            </dl>
          )}
          {hasAddress && (
            <div>
              <p className="mb-1 text-caption text-neutral-700">Home address</p>
              <AddressBlock address={e} />
            </div>
          )}
        </CardBody>
      </details>
    </Card>
  );
}

/* ─────────────────────────────────────────────────────────────── */

function EmploymentTab({
  employee, plantName, actions,
}: { employee: Employee; plantName: (id?: string | null) => string; actions?: React.ReactNode }) {
  const history = useQuery<EmploymentRecordRow[]>(
    () => api.get<EmploymentRecordRow[]>(`/employees/${employee.id}/employment-history`),
    [employee.id],
  );

  return (
    <Card>
      <CardHeader
        title="Employment history"
        hint="Append-only. A transfer or a status change adds a row; it never rewrites the last one."
      />
      {/* Beside the history they write to, and wrapping rather than
          squeezing: the card header's action slot does not shrink. */}
      {actions && <div className="-mt-1 flex flex-wrap items-center gap-2 px-5 pb-4">{actions}</div>}
      <DataTable<EmploymentRecordRow>
        rows={history.data ?? []}
        loading={history.loading}
        error={history.error}
        onRetry={history.reload}
        rowKey={(r) => r.id}
        empty={{
          title: 'No employment history recorded',
          hint: 'History starts at the joining record. Imported employees may predate it.',
        }}
        columns={[
          {
            key: 'change', header: 'Change',
            cell: (r) => (
              <span className="font-medium text-neutral-900">
                {EMPLOYMENT_CHANGE_LABEL[r.changeType] ?? humanize(r.changeType)}
              </span>
            ),
          },
          { key: 'from', header: 'Effective', cell: (r) => day(r.effectiveFrom) },
          { key: 'plant', header: 'Plant', cell: (r) => plantName(r.plantId) },
          {
            key: 'reason', header: 'Reason',
            cell: (r) => <span className="text-small text-neutral-600">{r.reason ?? '—'}</span>,
          },
        ]}
      />
    </Card>
  );
}

function CasesTab({ list, canArrange, onArrange }: {
  list: { data: CursorPage<ExaminationRow> | null; loading: boolean; error: string | null; reload: () => void };
  canArrange: boolean;
  onArrange: () => void;
}) {
  const nav = useNavigate();
  return (
    <Card>
      <CardHeader
        title="Medical cases"
        hint="Where each case has reached. HR follows progress and the outcome; it does not see findings."
      />
      <DataTable<ExaminationRow>
        rows={list.data?.items ?? []}
        loading={list.loading}
        error={list.error}
        onRetry={list.reload}
        rowKey={(r) => r.id}
        onRowClick={(r) => nav(`/hr/examinations/${r.id}`)}
        empty={{
          title: 'No medical cases for this person',
          hint: canArrange ? 'Schedule one, or include them in a campaign.' : 'Cases appear here once one is arranged.',
          action: canArrange ? <Button onClick={onArrange}>Schedule checkup</Button> : undefined,
        }}
        columns={[
          {
            key: 'type', header: 'Type',
            cell: (r) => <span className="font-medium text-neutral-900">{EXAM_TYPE_LABEL[r.examinationType] ?? humanize(r.examinationType)}</span>,
          },
          {
            key: 'status', header: 'State', mobileLabel: false,
            cell: (r) => <StatusBadge tone={caseStatusTone(r.status, r.outcome)}>{caseStatusLabel(r.status, r.outcome)}</StatusBadge>,
          },
          { key: 'raised', header: 'Raised', cell: (r) => day(r.createdAt) },
          {
            key: 'due', header: 'Due', align: 'right',
            cell: (r) => (
              <span>
                {day(r.dueDate)}
                {r.slaBreachedAt && <StatusBadge tone="overdue" className="ml-2">Late</StatusBadge>}
              </span>
            ),
          },
        ]}
      />
    </Card>
  );
}

function CertificatesTab({ list }: {
  list: { data: CursorPage<CertificateRow> | null; loading: boolean; error: string | null; reload: () => void };
}) {
  const nav = useNavigate();
  const { permissions } = useAuth();
  const [downloading, setDownloading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * The link is minted per click and expires in five minutes. A permanent URL
   * to a medical document is a URL that leaks forever.
   */
  const download = async (certificateId: string) => {
    setDownloading(certificateId);
    setError(null);
    try {
      const grant = await api.get<{ url: string }>(`/certificates/${certificateId}/download`);
      window.open(grant.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Card>
      <CardHeader
        title="Fitness certificates"
        hint="The outcome and how long it holds. The decision behind one belongs to the physician."
      />
      {error && <div className="px-4 pb-3"><Alert tone="danger">{error}</Alert></div>}
      <DataTable<CertificateRow>
        rows={list.data?.items ?? []}
        loading={list.loading}
        error={list.error}
        onRetry={list.reload}
        rowKey={(c) => c.id}
        onRowClick={(c) => nav(`/hr/certificates/${c.id}`)}
        empty={{
          title: 'No certificates issued',
          hint: 'A certificate is issued when a physician signs the fitness decision.',
        }}
        columns={[
          {
            key: 'decision', header: 'Fitness',
            cell: (c) => (
              <div>
                <StatusBadge tone={fitnessTone(c.decision)}>{FITNESS_LABEL[c.decision as Decision] ?? humanize(c.decision)}</StatusBadge>
                <div className="mt-0.5 font-mono text-caption text-neutral-500">{c.certificateNumber}</div>
              </div>
            ),
          },
          { key: 'issued', header: 'Issued', cell: (c) => day(c.issuedAt) },
          { key: 'until', header: 'Valid until', cell: (c) => day(c.validUntil) },
          {
            key: 'status', header: 'Status', mobileLabel: false,
            cell: (c) => {
              const st = c.expiryState ? CERT_STATE[c.expiryState] : { label: humanize(c.status), tone: 'neutral' as Tone };
              return <StatusBadge tone={st.tone}>{st.label}</StatusBadge>;
            },
          },
          {
            key: 'download', header: 'Actions', align: 'right', alwaysVisible: true, mobileLabel: '',
            cell: (c) => (
              <RowActions>
                <IconAction label={`View certificate ${c.certificateNumber}`} icon={Eye} onClick={() => nav(`/hr/certificates/${c.id}`)} />
                {permissions.has('certificate.download') && (
                  <IconAction
                    label={c.generationStatus !== 'ISSUED'
                      ? `Certificate ${c.certificateNumber}: the PDF is still being generated`
                      : `Download certificate ${c.certificateNumber} (PDF)`}
                    icon={Download}
                    loading={downloading === c.id}
                    disabled={c.generationStatus !== 'ISSUED'}
                    onClick={() => void download(c.id)}
                  />
                )}
              </RowActions>
            ),
          },
        ]}
      />
    </Card>
  );
}

/* ── Actions ──────────────────────────────────────────────────── */

function TransferDialog({
  employee, plants, onClose, onDone,
}: {
  employee: Employee;
  plants: Plant[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [plantId, setPlantId] = useState(employee.plantId ?? '');
  const [plant, setPlant] = useState<Plant | null>(null);
  const [effectiveFrom, setEffectiveFrom] = useState(localDay);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const moving = plantId && plantId !== employee.plantId;

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      await api.post(`/employees/${employee.id}/transfer`, {
        plantId,
        effectiveFrom,
        reason,
        version: employee.version,
      });
      onDone();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <Dialog title={`Transfer ${employee.fullName}`} onClose={onClose}>
      <div className="space-y-4">
        {error && <Alert tone="danger">{error}</Alert>}

        <EntityCombobox<Plant>
          label="New plant" required
          value={plant}
          onChange={(v) => {
            setPlant(v);
            setPlantId(v?.id ?? '');
          }}
          fetchOptions={(q, cursor) => pageAllPlants<Plant>(
            `/plants?limit=20&q=${encodeURIComponent(q)}`, cursor)}
          getKey={(x) => x.id}
          placeholder="Select the receiving plant"
          searchPlaceholder="Plant name or code"
          renderOption={(x) => `${x.code} — ${x.name}`}
        />

        <TextField
          label="Effective from" type="date" value={effectiveFrom}
          onChange={setEffectiveFrom} required
        />
        <TextField
          label="Reason" value={reason} onChange={setReason} required
          hint="Recorded on the employment history row, which is not editable afterwards."
        />

        {moving && (
          <Alert tone="info" title="History is preserved">
            The previous plant stays on the record. Examinations already
            completed remain attributed to where they happened.
          </Alert>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            loading={busy}
            disabled={!plantId || reason.trim().length < 3}
            onClick={submit}
          >
            Transfer
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function ExitDialog({
  employee, onClose, onDone,
}: { employee: Employee; onClose: () => void; onDone: () => void }) {
  const [effectiveFrom, setEffectiveFrom] = useState(localDay);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      await api.post(`/employees/${employee.id}/deactivate`, {
        effectiveFrom, reason, version: employee.version,
      });
      onDone();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <Dialog title={`Record exit — ${employee.fullName}`} onClose={onClose}>
      <div className="space-y-4">
        {error && <Alert tone="danger">{error}</Alert>}

        <p className="text-small text-neutral-600">
          Marks {employee.fullName} as exited and closes their sign-in to the
          employee app. Their examinations, certificates and consents are kept.{' '}
          <InfoTip label="About records after exit">
            The record of a statutory medical does not stop existing because somebody left.
          </InfoTip>
        </p>

        <TextField
          label="Last working day" type="date" value={effectiveFrom}
          onChange={setEffectiveFrom} required
        />
        <TextField
          label="Reason" value={reason} onChange={setReason} required
          placeholder="Resigned, end of contract, retirement…"
          info="Recorded on the employment history row and in the audit trail."
        />

        <Alert tone="warning" title="This is not reversible here">
          Re-employing someone is recorded as a new joining, not as an undo. Use
          Change status for a temporary absence instead.
        </Alert>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="danger" loading={busy} disabled={reason.trim().length < 3} onClick={submit}>
            Record exit
          </Button>
        </div>
      </div>
    </Dialog>
  );
}


function StatusDialog({
  employee, onClose, onDone,
}: { employee: Employee; onClose: () => void; onDone: () => void }) {
  const [status, setStatus] = useState(employee.status === 'ACTIVE' ? 'ON_LEAVE' : 'ACTIVE');
  const [effectiveFrom, setEffectiveFrom] = useState(localDay);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      await api.post(`/employees/${employee.id}/status`, {
        status, effectiveFrom, reason, version: employee.version,
      });
      onDone();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <Dialog title={`Change status — ${employee.fullName}`} onClose={onClose}>
      <div className="space-y-4">
        {error && <Alert tone="danger">{error}</Alert>}

        <SelectField label="New status" value={status} onChange={setStatus} required>
          <option value="ACTIVE">Active</option>
          <option value="ON_LEAVE">On leave</option>
          <option value="SUSPENDED">Suspended</option>
        </SelectField>

        <TextField
          label="Effective from" type="date" value={effectiveFrom}
          onChange={setEffectiveFrom} required
        />
        <TextField
          label="Reason" value={reason} onChange={setReason} required
          info="Each change writes its own history row, so an absence reads as an interval."
        />

        {status === 'ON_LEAVE' && (
          <Alert tone="info">
            An employee on leave is still counted as owed their statutory
            examination. They stay in the compliance figures.
          </Alert>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button loading={busy} disabled={reason.trim().length < 3} onClick={submit}>
            Save
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
